/**
 * Sign-in route — MUI email/password form + Google popup button.
 *
 * On success, Firebase fires `onAuthStateChanged`, the AuthProvider updates
 * `useUser()`, and the `_authed` layout lets the user proceed to `/`.
 */
import GoogleIcon from "@mui/icons-material/Google";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useAuth, useUser } from "@/auth/AuthProvider";

function friendlyError(err: unknown): string {
  const code = (err as { code?: string }).code;
  if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
    return "Wrong password. Please try again.";
  }
  if (code === "auth/user-not-found") {
    return "No account exists with that email.";
  }
  if (code === "auth/invalid-email") {
    return "That email address is not valid.";
  }
  if (code === "auth/popup-closed-by-user") {
    return "Sign-in was cancelled.";
  }
  return "Sign-in failed. Please try again.";
}

function SignInPage() {
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const user = useUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Once a user is present, send them home.
  useEffect(() => {
    if (user) {
      void navigate({ to: "/" });
    }
  }, [user, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: "center" }}>
          Sign In
        </Typography>

        {error ? (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        ) : null}

        <Box component="form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={submitting} fullWidth>
              Sign In
            </Button>
          </Stack>
        </Box>

        <Divider>or</Divider>

        <Button
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={() => void handleGoogle()}
          disabled={submitting}
          fullWidth
        >
          Sign in with Google
        </Button>
      </Stack>
    </Container>
  );
}

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
});
